/**
 * @file Attachments Service
 * @description API endpoints for investment attachment operations.
 * Handles file uploads, downloads, and metadata management.
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type {
  InvestmentAttachmentMeta,
  AttachmentExtractedText,
  AttachmentUpdateDto,
} from '@medsuite/models';

/**
 * DTO for uploading a new attachment
 */
export interface UploadAttachmentDto {
  file: File;
  title?: string;
  description?: string;
}

/**
 * Response for list of attachments
 */
export interface AttachmentsListResponse {
  investment_id: number;
  attachments: InvestmentAttachmentMeta[];
  total: number;
}

/**
 * Response for extracted texts
 */
export interface ExtractedTextsResponse {
  investment_id: number;
  documents: AttachmentExtractedText[];
  total: number;
}

/**
 * Attachments Service
 */
class AttachmentsService {
  /**
   * Get all attachments for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<AttachmentsListResponse>> {
    return apiClient.get<AttachmentsListResponse>(`/investments/${investmentId}/attachments`);
  }

  /**
   * Upload a new attachment
   */
  async upload(
    investmentId: number,
    data: UploadAttachmentDto
  ): Promise<ApiResponse<InvestmentAttachmentMeta>> {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.title) {
      formData.append('title', data.title);
    }
    if (data.description) {
      formData.append('description', data.description);
    }

    const response = await apiClient.getAxiosInstance().post<InvestmentAttachmentMeta>(
      `/investments/${investmentId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  /**
   * Update attachment metadata
   */
  async update(
    investmentId: number,
    attachmentId: number,
    data: AttachmentUpdateDto
  ): Promise<ApiResponse<InvestmentAttachmentMeta>> {
    return apiClient.put<InvestmentAttachmentMeta>(
      `/investments/${investmentId}/attachments/${attachmentId}`,
      data
    );
  }

  /**
   * Delete an attachment
   */
  async delete(investmentId: number, attachmentId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/attachments/${attachmentId}`);
  }

  /**
   * Get download URL for an attachment
   */
  getDownloadUrl(investmentId: number, attachmentId: number): string {
    // This returns a URL that can be used directly for downloads
    const baseUrl = apiClient.getAxiosInstance().defaults.baseURL || '';
    return `${baseUrl}/investments/${investmentId}/attachments/${attachmentId}/download`;
  }

  /**
   * Download an attachment (returns blob)
   */
  async download(investmentId: number, attachmentId: number): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(
      `/investments/${investmentId}/attachments/${attachmentId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * Get extracted texts for AI context
   */
  async getExtractedTexts(investmentId: number): Promise<ApiResponse<ExtractedTextsResponse>> {
    return apiClient.get<ExtractedTextsResponse>(
      `/investments/${investmentId}/attachments/extracted-text`
    );
  }
}

export const attachmentsService = new AttachmentsService();
